/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  get,
  isNil,
  chain,
  fill,
  map,
  merge,
  isEmpty,
  includes,
  isEqual,
  isString,
  concat,
  reduce,
  flatMap,
  isFunction,
  chunk,
  isNumber,
  isArray,
  size,
  values,
  compact,
  isPlainObject,
  has,
  keys,
  lte,
  filter,
  pick,
  every,
} from 'lodash';
import {
  ContentText,
  ContentColumns,
  ContentTable,
  Column,
} from 'pdfmake/interfaces';

import { MARK_SIGNATURE } from '@config/global-constants';
import { flatTreeDataByHasChildren } from '@app/shared/utils/common-utils';

import { AbstractPdfContentBuilder } from './abstract.pdf-content-builder';
import {
  IPdfGrid,
  IPdfOptions,
  IPdfColumn,
  IPdfGridColHeader,
  PdfSignature,
  PdfTitle,
  PdfData,
  IPdfGridValueParams,
  IPdfTitle,
  PdfContent,
  IPdfDefinitionsWithInnerProperty,
  IPdfValueParams,
  IPdfSummaryField,
  PdfSummary,
  IPdfArrSummary,
  IPdfSimpleSummary,
  IPdfGridOptions,
  IColSpanContentText,
  PdfSimpleText,
  IPdfDefinitions,
  IPdfSignatureColumn,
} from '../pdf.type';
import { PDF_COLORS } from '../constants';
import {
  getSelectedColumns,
  isEffectThisGrid,
} from '../utils/pdf-column-selector.utils';
import {
  getOrderedData,
  generateSignatureLine,
  buildSignatureText,
} from '../utils';
import { IPdfGridData, IPdfData } from '../pdf-data.type';
import { I18n } from '@app/i18n/strings-keys';

class DefaultPdfContentBuilder extends AbstractPdfContentBuilder {
  protected emptySummary: ContentColumns = {
    margin: [0, 0, 0, 5],
    columns: [],
  };

  protected signatureConfig: IPdfSignatureColumn = {
    title: {
      text: I18n.Common.Notes,
      fontSize: 11,
      bold: true,
      margin: [0, 10, 0, -10],
      alignment: 'left',
    },
  };

  protected signatureTitleConfigPath: string[] = [
    'options',
    'columnSelector',
    'signatureConfiguration',
    'title',
  ];

  public buildContentSimpleText(
    text: PdfSimpleText,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): ContentText[] {
    const texts: ContentText[] = map(
      isArray(text) ? text : [text],
      (singleText) => {
        return <ContentText>{
          ...singleText,
          text: this.services.i18nService.instant(singleText.text),
          fontSize: singleText.fontSize ?? 11,
        };
      },
    );

    return texts;
  }

  public buildSimpleSummary(
    summary: IPdfSimpleSummary,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    addTrailEmpty: boolean = false,
  ): ContentColumns[] {
    const summaryFields = get(summary, 'fields', {});

    if (
      isNil(summary) ||
      isNil(summary.fieldsPerRow) ||
      isEmpty(summaryFields)
    ) {
      return;
    }
    const fieldsPerRow = get(summary, 'fieldsPerRow', 3);
    const defaultFieldWidth = `${(100 / fieldsPerRow).toFixed(2)}%`;

    const defaultSummaryColumns = fill(Array(fieldsPerRow), {});

    const fctParams: IPdfValueParams = {
      data,
      tools: pdfDefinition.tools,
      value: null,
    };

    return chain<Record<string, IPdfSummaryField>>(summaryFields)
      .toPairs()
      .chunk(fieldsPerRow)
      .map((groupedFields) => {
        const columns = map(groupedFields, ([fieldName, field]) => {
          const textValue = isFunction(field.valueGetter)
            ? field.valueGetter({
                ...fctParams,
                value: get(data, get(field, 'path', fieldName), ''),
              })
            : get(data, field.path, '');

          const fieldFctParams = { ...fctParams, value: textValue };

          const isHiddenThisField = isFunction(field.hidden)
            ? field.hidden(fieldFctParams)
            : field.hidden;

          // hidden this, return null
          if (isHiddenThisField) {
            return null;
          }

          const formattedTextValue = isFunction(field.valueFormatter)
            ? field.valueFormatter(fieldFctParams)
            : textValue;

          return <ContentText>{
            style: field.style,
            text: [
              {
                text: this.services.i18nService.instant(
                  get(field, 'title', fieldName),
                ),
              },
              { text: ': ' },
              {
                text: formattedTextValue,
                bold: true,
              },
            ],
            width: get(field, 'width', defaultFieldWidth),
          };
        });

        const allValidColumns = compact(columns);

        // not have any valid column, just return
        if (isEmpty(allValidColumns)) {
          return;
        }

        return <ContentColumns>{
          ...this.emptySummary,
          style: isEmpty(allValidColumns) ? this.emptySummary.style : 'summary',
          columns: merge([], defaultSummaryColumns, allValidColumns),
        };
      })
      .concat(addTrailEmpty ? [this.emptySummary] : [])
      .compact()
      .value();
  }

  public buildArrSummary(
    summary: IPdfArrSummary,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): ContentColumns[] {
    const summaryFields = get(summary, 'fields', []);
    if (
      isNil(summary) ||
      isPlainObject(summaryFields) ||
      isEmpty(summaryFields)
    ) {
      return;
    }

    return chain(summaryFields)
      .flatMap((summaryRecord) => {
        return this.buildSimpleSummary(
          { fieldsPerRow: size(keys(summaryRecord)), fields: summaryRecord },
          data,
          pdfDefinition,
        );
      })
      .concat([this.emptySummary])
      .compact()
      .value();
  }

  public buildContentSummary(
    summary: PdfSummary,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): ContentColumns[] {
    if (isNil(summary)) {
      return;
    }

    if (has(summary, 'fieldsPerRow') && !isArray(summary.fields)) {
      return this.buildSimpleSummary(
        summary as IPdfSimpleSummary,
        data,
        pdfDefinition,
        true,
      );
    }

    if (!has(summary, 'fieldsPerRow') && isArray(summary.fields)) {
      return this.buildArrSummary(
        summary as IPdfArrSummary,
        data,
        pdfDefinition,
      );
    }
  }

  public buildContentGridHeader(
    col: IPdfColumn,
    options: IPdfOptions,
  ): IColSpanContentText {
    return {
      colSpan: size(col.children) || 1,
      text: this.services.i18nService.instant(
        isString(col.header) ? col.header : col.header?.text || col.title,
      ),
      alignment:
        (col.header as IPdfGridColHeader)?.alignment || col.alignment || 'left',
      bold: !options.useSmallerFontInTable,
      color: PDF_COLORS.fontColor,
      fontSize: get(
        options,
        'gridHeaderFontSize',
        options.useSmallerFontInTable ? 7 : 10,
      ),
      fillColor: PDF_COLORS.headerBgColor,
    };
  }

  public buildContentGridHeaderWithChildren(
    col: IPdfColumn,
    options: IPdfOptions,
  ): IColSpanContentText[][] {
    return [
      [
        this.buildContentGridHeader(col, options),
        ...Array.from(
          { length: size(col.children) - 1 },
          () => ({}) as IColSpanContentText,
        ),
      ],
      map(col.children, (childCol) =>
        this.buildContentGridHeader(childCol, options),
      ),
    ];
  }

  public buildContentGridCell(
    col: IPdfColumn,
    valueParams: IPdfGridValueParams,
    options: IPdfOptions,
  ): PdfContent {
    const originValue = get(valueParams.rowData, col.path);
    const fnParams: IPdfGridValueParams = {
      ...valueParams,
      value: originValue,
    };

    fnParams.value = isFunction(col?.valueGetter)
      ? col.valueGetter(fnParams)
      : fnParams.value;

    const colStyle = isFunction(col.style) ? col.style(fnParams) : col.style;

    const colContent = isFunction(col.builder)
      ? col.builder(col, fnParams, options)
      : {
          text: (isFunction(col?.valueFormatter)
            ? col.valueFormatter(fnParams)
            : fnParams.value
          )?.toString(),
        };

    return map(isArray(colContent) ? colContent : [colContent], (content) =>
      !isPlainObject(content)
        ? content
        : merge<Partial<PdfContent>, PdfContent, Partial<ContentText>>(
            {
              bold: false,
              alignment: col?.alignment || 'left',
              color: get(col, 'style.color', PDF_COLORS.fontColor),
              fontSize: options.useSmallerFontInTable ? 5 : 9, // #TODO confirm is this possible
              // fillColor: colStyle?.fillColor || PDF_COLORS.whiteBgColor,
              // #TODO confirm is this useful
              style: colStyle,
              rowSpan: col.rowSpan,
              colSpan: col.colSpan,
            },
            content,
            { ...colStyle },
          ),
    );
  }

  public buildContentGridWidth(
    col: IPdfColumn,
    colIndex: number,
    options: IPdfOptions,
  ): (string | number)[] {
    if (size(col.children) > 1) {
      return map(col.children, (childrenItem) =>
        get(childrenItem, 'width', '*'),
      );
    }
    if (!isEmpty(col?.width?.toString())) {
      return [col.width];
    }

    const notSmallerFontColWidths = ['20%', '*'];
    let width: number | string = options.defaultEqualColumnWidth ? '*' : 'auto';

    // The first column displays the name (such as inventory item name),
    // and the content may be relatively long, so configure the width of the first column separately.
    if (options.useSmallerFontInTable && isEqual(colIndex, 1)) {
      width = 40;
    }

    if (!options.useSmallerFontInTable && includes([0, 1], colIndex)) {
      width = notSmallerFontColWidths[colIndex];
    }

    return [width];
  }

  public getGridTitle(
    title: PdfTitle,
    data: PdfData,
    gridData: IPdfGridData[],
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): string | string[] {
    if (isNil(title)) {
      return;
    }

    if (isString(title)) {
      return this.services.i18nService.instant(title);
    }

    const gridTitleParams: IPdfGridValueParams = {
      data,
      gridData,
      tools: pdfDefinition?.tools,
      value: null,
    };

    if (isFunction(title)) {
      return this.services.i18nService.translateArrayLikeText(
        title(gridTitleParams),
      );
    }

    return this.services.i18nService.translateArrayLikeText(
      isFunction(title?.text) ? title.text(gridTitleParams) : title.text,
    );
  }

  public handleHiddenColumns(
    grid: IPdfGrid,
    fnParams: IPdfGridValueParams,
  ): IPdfColumn[] {
    const gridColumns: IPdfColumn[] = reduce(
      grid.columns,
      (cols, col) => {
        const isShownColumn = !(isFunction(col.hiddenColumn)
          ? col.hiddenColumn(fnParams)
          : col.hiddenColumn);

        if (isShownColumn) {
          const columnDefaultStyle = isFunction(grid.columnDefaultStyle)
            ? grid.columnDefaultStyle(col, fnParams.gridData, fnParams.data)
            : grid.columnDefaultStyle;

          // expanded default col style here
          cols.push(merge({}, columnDefaultStyle, col));
        }

        return cols;
      },
      [],
    );

    return gridColumns;
  }

  public buildContentGrid(
    gridOptions: IPdfGridOptions,
    data: IPdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
  ): (ContentTable | ContentText | PdfContent)[] {
    const { grid, gridData } = gridOptions;
    const options = pdfDefinition?.options;

    const orderedData = getOrderedData(
      gridData,
      grid?.orderBy,
      grid.orderByCaseInsensitive,
    );

    const fnParams: IPdfGridValueParams = {
      data,
      gridData: orderedData,
      tools: pdfDefinition.tools,
      options: pdfDefinition.options,
      value: null,
    };
    const gridColumns = this.handleHiddenColumns(grid, fnParams);
    // get column selector selected columns
    const selectedColumns = isEffectThisGrid(gridOptions, pdfDefinition.options)
      ? getSelectedColumns(gridColumns, options)
      : gridColumns;
    const [colWidths, headerRows] = reduce(
      selectedColumns,
      (preRows, col, index) => {
        const [widths, headers] = preRows;
        const [firstHeaderRow, secondHeaderRow] =
          this.buildContentGridHeaderWithChildren(col, options);

        return [
          concat(widths, ...this.buildContentGridWidth(col, index, options)),
          // concat(headers, this.buildContentGridHeader(col, options)),
          [
            concat([], headers[0] || [], firstHeaderRow),
            concat([], headers[1] || [], secondHeaderRow),
          ],
        ];
      },
      [[], []],
    );
    if (isEmpty(colWidths)) {
      return;
    }

    // need split row data from base on path to multi-rows
    const defaultSplitter = (rowDataList): [] =>
      get(rowDataList, grid.multiRow.baseOnRowFieldPath, []);

    const isNeedSplitGridData = !isEmpty(grid.multiRow?.baseOnRowFieldPath);

    // split rows
    const [rowsAfterSplit, rowSpans]: [IPdfGridData[], number[]] = reduce(
      orderedData,
      ([preSplitData, preRowSpans], curRowData) => {
        let splitRowData = [curRowData];
        if (isNeedSplitGridData) {
          splitRowData = (
            isFunction(grid.multiRow?.splitter)
              ? grid.multiRow.splitter
              : defaultSplitter
          )(curRowData, gridData, grid, data);
          preSplitData.push(
            ...map(splitRowData, (rowData, index) => {
              const isFirstEle = isEqual(index, 0);

              // only first element need to set span as total split data length
              preRowSpans.push(isFirstEle ? size(splitRowData) : 1);

              return isFirstEle
                ? {
                    ...curRowData,
                    ...rowData,
                  }
                : rowData;
            }),
          );
        } else {
          preSplitData.push(curRowData);
          preRowSpans.push(1);
        }

        return [preSplitData, preRowSpans];
      },
      [[], []],
    );

    // build rows
    const cellRowsArr: ContentText[][] = chain(rowsAfterSplit)
      .map((rowData, rowIndex) => {
        /**
         * get row style
         */
        const rowFnParams = { ...fnParams, rowData, rowIndex };

        const isHiddenRow = isFunction(grid.hiddenRow)
          ? grid.hiddenRow(rowFnParams)
          : grid.hiddenRow;

        // hidden this row
        if (isHiddenRow) {
          return null;
        }

        const rowStyle = isFunction(grid.rowStyle)
          ? grid.rowStyle(rowFnParams)
          : grid.rowStyle;

        return reduce(
          flatTreeDataByHasChildren(selectedColumns),
          (preCells, col) => {
            return concat(
              preCells,
              this.buildContentGridCell(
                merge(
                  {
                    style: rowStyle,
                  },
                  col,
                  {
                    rowSpan: col.rowSpan || get(rowSpans, rowIndex),
                  },
                  {
                    colSpan: col.colSpan || 1,
                  },
                ),
                rowFnParams,
                options,
              ),
            );
          },
          [],
        );
      })
      // filter all empty row(hidden row)
      .compact()
      .value();

    const isHaveSubTitle = !isNil(grid.subTitle);

    const subTitle = isHaveSubTitle
      ? this.getGridTitle(grid.subTitle, data, gridData, pdfDefinition)
      : [];

    const tableSubTitle: ContentText[] = isHaveSubTitle
      ? map(isArray(subTitle) ? subTitle : [subTitle], (title) => ({
          text: title,
          fontSize: 8,
          margin: [0, 0, 0, 5],
          ...(grid.subTitle as IPdfTitle)?.style,
        }))
      : [];

    const tableTitle: ContentText = {
      text: this.getGridTitle(grid.title, data, gridData, pdfDefinition),
      fontSize: 11,
      bold: true,
      // need cover sub title margin bottom when not have sub title
      margin: [0, 0, 0, isHaveSubTitle ? 5 : 10],
      ...(grid.title as IPdfTitle)?.style,
      style: grid.alwaysInOnePage ? MARK_SIGNATURE : undefined,
    };

    const table: ContentTable = {
      style: grid.alwaysInOnePage ? ['table', MARK_SIGNATURE] : 'table',
      table: {
        headerRows: grid.headerRows || 1,
        widths: colWidths,
        body: [...filter(headerRows, (item) => !isEmpty(item)), ...cellRowsArr],
        dontBreakRows: get(grid, 'dontBreakRows', true),
        heights: get(grid, 'height', 'auto'),
      },
      pageBreak: grid.pageBreak,
      layout: merge(
        {
          defaultBorder: true,
          hLineColor: PDF_COLORS.tableLineColor,
          vLineColor: PDF_COLORS.tableLineColor,
          hLineWidth: (): number => 1,
          vLineWidth: (): number => 1,
        },
        grid.layout,
      ),
    };

    return this.buildNestedContent(pdfDefinition, data, {
      options: merge(
        { curGridData: gridData },
        pick(gridOptions, [
          'preGridData',
          'nextGridData',
          'multiGridBuildIndex',
          'multiGridCount',
        ]),
      ),
      nestedContent: grid.nestedContent,
      baseOnContent: compact([tableTitle, ...tableSubTitle, table]),
    });
  }

  public buildContentGrids(
    grids: Record<string, IPdfGrid>,
    data: PdfData,
    pdfDefinition: IPdfDefinitionsWithInnerProperty,
    contentKey: string,
  ): (ContentTable | ContentText | PdfContent)[] {
    return flatMap(grids, (grid, fieldPath) => {
      // Check if grid is hidden
      if (isFunction(grid.hidden)) {
        const isHidden = grid.hidden({
          data,
          gridData: [],
          tools: pdfDefinition.tools,
          options: pdfDefinition.options,
          value: null,
        });

        if (isHidden) {
          return [];
        }
      }

      const fieldGridData: [] | {} = get(data, fieldPath, []);
      // ensure grid data is array
      const gridData = isArray(fieldGridData) ? fieldGridData : [fieldGridData];

      let chunkedGridDataList = null;

      if (isNumber(grid.multiGrid)) {
        chunkedGridDataList = chunk(gridData, grid.multiGrid);
      }

      if (isFunction(grid.multiGrid)) {
        chunkedGridDataList = values(
          grid.multiGrid({ data: gridData, tools: pdfDefinition.tools }),
        );
      }

      const multiGridCount = size(chunkedGridDataList);
      const gridOptions: IPdfGridOptions = {
        grid,
        gridData,
        fieldPath,
        contentKey,
        multiGridCount,
      };

      return isNil(chunkedGridDataList) || lte(multiGridCount, 1)
        ? this.buildContentGrid(gridOptions, data, pdfDefinition)
        : flatMap(chunkedGridDataList, (chunkedGridData, gridIndex: number) =>
            this.buildContentGrid(
              {
                ...gridOptions,
                multiGridBuildIndex: gridIndex,
                preGridData: get(chunkedGridDataList, gridIndex - 1),
                nextGridData: get(chunkedGridDataList, gridIndex + 1),
                gridData: chunkedGridData,
              },
              data,
              pdfDefinition,
            ),
          );
    });
  }

  /**
   *
   * @notice when print signatures, default behavior is show signature
   * if do not want to show signature,
   * you just off switch or config 'definitions.options.signature_lines' as false
   */
  public buildContentSignatures(
    signatures: PdfSignature[][] | PdfSignature[],
    pdfData?: IPdfData,
    definitions?: IPdfDefinitions,
  ): PdfContent[] {
    const supportSignatureTitle = !!get(
      definitions,
      this.signatureTitleConfigPath,
      false,
    );
    let title = get(
      definitions,
      this.signatureTitleConfigPath,
      this.signatureConfig.title,
    );

    const enableSignatureLines = !!get(
      definitions.options,
      'signature_lines',
      true,
    );
    // if return empty content, in pdf content builder will not execute buildContentSignatures
    if (!enableSignatureLines || !signatures || isEmpty(signatures)) {
      return [];
    }
    if (title?.text) {
      title = merge({}, this.signatureConfig.title, title);
      title.text = this.services.i18nService.t(title.text);
    }

    const defaultSignatureColumn: Column = {
      width: '*',
      margin: [0, 15, 0, 0],
      fontSize: 9,
      alignment: 'right',
      text: '',
    };
    const defaultSignature: ContentColumns = {
      style: MARK_SIGNATURE,
      columns: [defaultSignatureColumn],
    };
    // Helper to build a single signature column
    const buildSignatureColumn = (signature: PdfSignature): Column => {
      if (isString(signature)) {
        return {
          ...defaultSignatureColumn,
          text: buildSignatureText(
            this.services.i18nService.instant(signature),
          ),
        };
      }

      const signatureLine = signature?.withoutSignatureLine
        ? ''
        : generateSignatureLine(
            signature.signatureLineLength,
            signature.signatureSpaceLength,
          );

      const signatureStyle = signature?.style || {};

      return merge({}, defaultSignatureColumn, {
        ...signature,
        ...signatureStyle,
        text: buildSignatureText(
          this.services.i18nService.instant(signature.text),
          signatureLine,
        ),
      });
    };

    // Normalize to 2D array
    const normalizedSignatures: (PdfSignature | string)[][] =
      isArray(signatures) && every(signatures, isArray)
        ? (signatures as (PdfSignature | string)[][])
        : (signatures as (PdfSignature | string)[]).map((sig) => [sig]);

    // if setting return empty content, in pdf content builder will not execute buildContentSignatures.
    // So here we don't need to check if we need title or not.
    // Currently, title is always configured for which opening signature line switch.
    return [
      supportSignatureTitle ? { style: MARK_SIGNATURE, ...title } : {},
      ...map(normalizedSignatures, (rowSignatures) => {
        return merge({}, defaultSignature, {
          style: MARK_SIGNATURE,
          columns: map(rowSignatures, buildSignatureColumn),
        });
      }),
    ];
  }

  public buildContentTips(tips: string[]): PdfContent {
    return {
      columns: map(tips, (text) => ({
        text: this.services.i18nService.instant(text),
        width: '*',
        margin: [0, 0, 0, 8],
        fontSize: 9,
        alignment: 'left',
      })),
    };
  }
}

export { DefaultPdfContentBuilder };
